// Section weights for the weighted final score
const WEIGHTS = { A: 0.20, B: 0.35, C: 0.30, D: 0.15 };

// Maturity tiers from highest to lowest — find() returns first match
const TIERS = [
  {
    min: 76,
    label: 'AI Leader',
    key: 'leader',
    description: 'Your organisation is well-positioned for AI transformation. You have the data, processes, and strategic intent in place — the priority is scaling what already works.',
  },
  {
    min: 51,
    label: 'AI Developing',
    key: 'developing',
    description: 'You have meaningful AI or automation in place, but it is not yet systematic. The opportunity is to connect the dots and build a coherent AI strategy across departments.',
  },
  {
    min: 26,
    label: 'AI Aware',
    key: 'aware',
    description: 'Awareness and interest exist, but implementation is limited. Focused quick wins in 1–2 high-impact areas will build momentum and organisational confidence.',
  },
  {
    min: 0,
    label: 'AI Novice',
    key: 'novice',
    description: 'You are at the beginning of your AI journey. The best starting point is establishing clean data, clear ownership, and one high-visibility pilot that proves the value.',
  },
];

// Normalize a single question answer to 0–100
const scoreOneQuestion = (q, value) => {
  if (q.isDepartmentSelect) return null; // routing question, excluded

  if (q.type === 'star') {
    if (!value || value < 1) return null;
    return ((value - 1) / 4) * 100;
  }

  if (q.type === 'slider') {
    const v = value != null ? value : 5;
    return q.scoreInverted
      ? ((10 - v) / 9) * 100
      : ((v - 1) / 9) * 100;
  }

  if (q.type === 'radio') {
    if (!value) return null;
    const opt = q.options.find((o) => o.id === value);
    if (!opt) return null;
    const maxScore = Math.max(...q.options.map((o) => o.score));
    if (maxScore === 0) return null; // all options are zero (e.g. department selector)
    return (opt.score / maxScore) * 100;
  }

  return null;
};

// Average all scorable questions in an array
const avgSection = (questions, answers) => {
  const scored = questions
    .map((q) => scoreOneQuestion(q, answers[q.id]))
    .filter((s) => s !== null);
  if (!scored.length) return 0;
  return scored.reduce((sum, s) => sum + s, 0) / scored.length;
};

/**
 * calculateScores
 *
 * @param {Object} answers        { questionId: optionId | starValue | sliderValue }
 * @param {Object} surveyStructure canonical structure from surveyApi / surveyData
 *
 * @returns {{
 *   sectionScores: { A: number, B: number, C: number, D: number },
 *   finalScore: number,       // 0–100, weighted
 *   tierLabel: string,        // 'AI Leader' | 'AI Developing' | 'AI Aware' | 'AI Novice'
 *   tierKey: string,          // 'leader' | 'developing' | 'aware' | 'novice'
 *   tierDescription: string,
 * }}
 */
export const calculateScores = (answers, surveyStructure) => {
  const { sections, departmentKeys } = surveyStructure;

  // Resolve which department's Section B questions to score
  const deptQ = sections.A.find((q) => q.isDepartmentSelect);
  const deptOptionId = deptQ ? answers[deptQ.id] : null;
  const deptKey = deptOptionId ? (departmentKeys[deptOptionId] || null) : null;

  const rawA = avgSection(sections.A, answers);
  const rawB = deptKey ? avgSection(sections.B[deptKey] || [], answers) : 0;
  const rawC = avgSection(sections.C, answers);
  const rawD = avgSection(sections.D, answers);

  const finalScore = Math.round(
    rawA * WEIGHTS.A +
    rawB * WEIGHTS.B +
    rawC * WEIGHTS.C +
    rawD * WEIGHTS.D
  );

  const tier = TIERS.find((t) => finalScore >= t.min) || TIERS[TIERS.length - 1];

  return {
    sectionScores: {
      A: Math.round(rawA),
      B: Math.round(rawB),
      C: Math.round(rawC),
      D: Math.round(rawD),
    },
    finalScore,
    tierLabel: tier.label,
    tierKey: tier.key,
    tierDescription: tier.description,
  };
};
